import _ from 'lodash'
import {createReducer, createAction} from 'redux-act'
import axios from 'axios'
import qs from 'qs'
import l8n from 'l8n'

import {baseUrl} from 'scripts/env'

export const REDUCER = 'license'
//Action prefix
const NS = `@@${REDUCER}/`
//Actions
export const reset = createAction(`${NS}RESET`)

export const setLicense = createAction(`${NS}SET_LICENSE`)
export const setSiteId = createAction(`${NS}SET_SITE_ID`)
export const setTransactionId = createAction(`${NS}SET_TRANSACTION_ID`)
export const setClientTransactions = createAction(`${NS}SET_CLIENT_TRANSACTIONS`)
export const boletoBillCaptured = createAction(`${NS}BOLETO_BILL_CAPTURED`)

const initialState = {
  siteId: null,
  license: {
    pricelist: null,
    current: null,
    next: null,
    //billing plans
    plan: null,
    plans: [],
    available_upgrade_plans: [],
  },
  //transaction params
  transactionId: null,
}

export const fetchLicenseInformation = (siteId) => async (dispatch) => {
  dispatch(setSiteId(siteId))
  const response = await axios({
    method: 'GET',
    url: `${baseUrl}/sites/${siteId}/billing/license/fullInfo`
  })

  if (_.get(response, 'data.ok', false) === false) {
    throw new Error(l8n['license.error.gettingLicense'])
  }

  const license = {...response.data}
  dispatch(setLicense(license))
}

export const fetchTransactions = () => async (dispatch, getState) => {
  const siteId = getState().billing.siteId

  const response = await axios({
    method: 'GET',
    url: `${baseUrl}/sites/${siteId}/billing/transaction`
  })

  if (_.get(response, 'data.ok', false) === false) {
    throw new Error(l8n['license.error.onTransactionsHistory'])
  }

  const {transactions} = response.data

  return transactions
}

export const confirmSubscription = ({planId, period: term, usersCount, code: couponCode}) => async (dispatch, getState) => {
  const siteId = getState().billing.siteId
  let data = {usersCount, term}
  if (planId) {
    data = {...data, planId}
  }
  if (couponCode) {
    data = {...data, couponCode, productType: 'CHATS', useGlobal: 1}
  }

  const response = await axios({
    method: 'POST',
    url: `${baseUrl}/sites/${siteId}/billing/license/buy`,
    data: qs.stringify(data),
  })

  if (_.get(response, 'data.ok', false) === false) {
    throw new Error(l8n['license.error.onSubscription'])
  }

  const {transaction_id} = response.data
  dispatch(setTransactionId(transaction_id))
}

export const confirmUpgradeSubscription = (planId) => async (dispatch, getState) => {
  const siteId = getState().billing.siteId
  let data = {planId}

  const response = await axios({
    method: 'POST',
    url: `${baseUrl}/sites/${siteId}/billing/license/upgrade`,
    data: qs.stringify(data),
  })

  if (_.get(response, 'data.ok', false) === false) {
    throw new Error(l8n['license.error.onSubscription'])
  }

  const {transaction_id} = response.data
  dispatch(setTransactionId(transaction_id))
}

export const confirmAddOperators = ({period: term, usersCount, code: couponCode}) => async (dispatch, getState) => {
  const siteId = getState().billing.siteId
  let data = {usersCount}
  if (couponCode) {
    data = {...data, couponCode, term, productType: 'CHATS', useGlobal: 0}
  }

  const response = await axios({
    method: 'POST',
    url: `${baseUrl}/sites/${siteId}/billing/license/user/add`,
    data: qs.stringify(data),
  })

  if (_.get(response, 'data.ok', false) === false) {
    throw new Error(l8n['license.error.onOperators'])
  }

  const {transaction_id} = response.data
  dispatch(setTransactionId(transaction_id))
}

export const getDiscount = (code, term, transactionType) => async (dispatch, getState) => {
  const siteId = getState().billing.siteId
  const params = {
    couponCode: code,
    productType: 'CHATS',
    useGlobal: transactionType === 'NEW_LICENSE',
    transactionType,
    term
  }

  const response = await axios({
    method: 'GET',
    url: `${baseUrl}/sites/${siteId}/billing/license/coupon`,
    params,
  })

  if (_.get(response, 'data.ok', false) === false) {
    const apiMessage = _.get(response, 'response.violation_list.coupon', '')
    const message = mapApiCouponError(apiMessage)

    throw new Error(message)
  }

  return {...response.data}
}

const mapApiCouponError = (apiMessage) => {
  switch (apiMessage) {
    case 'coupon_already_used':
      return l8n['license.coupon.already_used'];
    case 'coupon_has_expired':
      return l8n['license.coupon.has_expired'];
    case 'coupon_is_inactive':
      return l8n['license.coupon.is_inactive'];
    default:
      return l8n['license.coupon.invalid_value'];
  }
}

export const printInvoice = (transactionId, useClientCurrency) => async (dispatch, getState) => {
  const siteId = getState().billing.siteId
  const params = {useClientCurrency: +useClientCurrency}

  const response = await axios({
    method: 'GET',
    url: `${baseUrl}/sites/${siteId}/billing/transaction/${transactionId}/bill`,
    params,
  })

  if (_.get(response, 'data.ok', false) === false) {
    throw new Error(l8n['license.error.onPrintInvoice'])
  }

  return response.data.bill_url
}

export const capturePagarTransaction = (transactionId, pagarToken, paymentMethod) => async (dispatch, getState) => {
  const siteId = getState().billing.siteId
  const data = {transactionId, pagarToken, paymentMethod}

  const response = await axios({
    method: 'POST',
    url: `${baseUrl}/sites/${siteId}/billing/transaction/pagar/capture`,
    data: qs.stringify(data),
  })

  if (_.get(response, 'data.ok', false) === false) {
    throw new Error(l8n['license.error.onPagar'])
  }

  dispatch(boletoBillCaptured())

  return response.data.boleto_url
}

export const createCustomBoletoTransaction = (transactionId, clientData, addressData) => async (dispatch, getState) => {
  const siteId = getState().billing.siteId
  const data = {
    client: clientData,
    address: addressData,
    payment_method: 'boleto'
  }

  const response = await axios({
    method: 'POST',
    url: `${baseUrl}/sites/${siteId}/billing/transaction/${transactionId}/pagar/payment`,
    data: qs.stringify(data),
  })

  if (_.get(response, 'data.ok', false) === false) {
    throw new Error(l8n['license.error.onPagar'])
  }

  dispatch(boletoBillCaptured())

  return response.data.boleto_url
}

export const fetchTransaction = (transactionId) => async (dispatch, getState) => {
  const siteId = getState().billing.siteId

  const response = await axios({
    method: 'GET',
    url: `${baseUrl}/sites/${siteId}/billing/transaction/${transactionId}`,
  })

  if (_.get(response, 'data.ok', false) === false) {
    throw new Error(l8n['license.error.onTransaction'])
  }

  return response.data.transaction
}

export default createReducer(
  {
    [reset]: (state) => ({ ...state, ...initialState }),

    [setLicense]: (state, license) => ({ ...state, license }),
    [setSiteId]: (state, siteId) => ({ ...state, siteId }),
    [setTransactionId]: (state, transactionId) => ({ ...state, transactionId }),
    [setClientTransactions]: (state, clientTransactions) => ({ ...state, clientTransactions }),
    [boletoBillCaptured]: (state) => ({ ...state }),
  },
  initialState
)



// WEBPACK FOOTER //
// ./src/ducks/billing.js